import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';

const SEV_CFG = {
  low:      { bg:'rgba(79,142,247,0.15)',  color:'var(--accent-primary)', label:'LOW'  },
  medium:   { bg:'rgba(245,158,11,0.15)',  color:'var(--accent-warning)', label:'MED'  },
  high:     { bg:'rgba(239,68,68,0.15)',   color:'var(--accent-danger)',  label:'HIGH' },
  critical: { bg:'rgba(239,68,68,0.28)',   color:'#ff4444',              label:'CRIT' },
};

const SevBadge = ({ sev }) => {
  const s = SEV_CFG[sev] || SEV_CFG.medium;
  return (
    <span style={{ fontSize:9, fontWeight:700, padding:'2px 6px', borderRadius:4, background:s.bg, color:s.color, letterSpacing:'0.5px' }}>
      {s.label}
    </span>
  );
};

const TrustBar = ({ score }) => {
  const pct = Math.min(100, Math.max(0, score || 0));
  const color = pct >= 75 ? 'var(--accent-success)' : pct >= 45 ? 'var(--accent-warning)' : 'var(--accent-danger)';
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
      <div style={{ flex:1, height:5, background:'var(--bg-elevated)', borderRadius:3, overflow:'hidden', minWidth:50 }}>
        <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:3, transition:'width 0.5s' }} />
      </div>
      <span style={{ fontSize:11, fontFamily:'var(--font-mono)', color, minWidth:30, fontWeight:600 }}>{Math.round(pct)}%</span>
    </div>
  );
};

// ── Student Card — memo to prevent unnecessary re-renders (#1 flicker fix) ────
const StudentCard = React.memo(function StudentCard({ student, selected, onSelect }) {
  const isIdle      = student.status === 'idle';
  const isOffline   = student.status === 'offline' || student.status === 'disconnected';
  const isSubmitted = student.status === 'submitted';
  const isTerm      = student.status === 'terminated';

  const ipColor = isTerm      ? 'var(--accent-danger)'
    : isSubmitted ? 'var(--accent-success)'
    : isOffline   ? 'var(--text-muted)'
    : isIdle      ? '#ffffff'
    : 'var(--accent-success)';

  const borderColor = selected ? 'var(--accent-primary)'
    : isTerm      ? 'rgba(239,68,68,0.5)'
    : isOffline   ? 'var(--border)'
    : isIdle      ? 'rgba(255,255,255,0.15)'
    : 'rgba(16,185,129,0.25)';

  const progress = student.totalQuestions > 0
    ? Math.round((student.answeredCount / student.totalQuestions) * 100) : 0;

  return (
    <div onClick={() => onSelect(student.attemptId)}
      style={{
        padding:'12px 14px', cursor:'pointer',
        background: selected ? 'rgba(79,142,247,0.08)' : 'var(--bg-card)',
        border:`1px solid ${borderColor}`,
        borderRadius:'var(--radius-sm)', position:'relative',
        opacity: isOffline ? 0.5 : 1,
      }}>
      {/* Live status dot */}
      <div style={{
        position:'absolute', top:10, right:10, width:8, height:8, borderRadius:'50%',
        background: isTerm ? 'var(--accent-danger)' : isSubmitted ? 'var(--accent-success)' : isOffline ? 'var(--text-muted)' : isIdle ? '#888' : 'var(--accent-success)',
        animation: !isIdle&&!isOffline&&!isSubmitted&&!isTerm ? 'blink 2s infinite' : 'none',
      }} />

      <div style={{ fontWeight:600, fontSize:13, marginBottom:3, paddingRight:18 }}>{student.name}</div>

      <div style={{ fontSize:11, fontFamily:'var(--font-mono)', color:ipColor, marginBottom:6, fontWeight: isIdle ? 400 : 600 }}>
        {student.ip || 'Unknown'}
        {isIdle      && <span style={{ marginLeft:6, fontSize:10, fontFamily:'var(--font-main)', color:'var(--text-muted)' }}>IDLE &gt;5min</span>}
        {isSubmitted && <span style={{ marginLeft:6, fontSize:10 }}> ✓ SUBMITTED</span>}
        {isTerm      && <span style={{ marginLeft:6, fontSize:10 }}> ⛔ TERMINATED</span>}
      </div>

      {!isOffline && (
        <>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'var(--text-muted)', marginBottom:3 }}>
            <span>Progress</span><span>{student.answeredCount||0}/{student.totalQuestions||'?'}</span>
          </div>
          <div style={{ height:3, background:'var(--bg-elevated)', borderRadius:2, overflow:'hidden', marginBottom:6 }}>
            <div style={{ height:'100%', width:`${progress}%`, background:'var(--accent-primary)', transition:'width 1s' }} />
          </div>
          <TrustBar score={student.trustScore ?? 100} />
          {(student.violations||0) > 0 && (
            <div style={{ marginTop:5, fontSize:11, color:'var(--accent-danger)' }}>
              ⚠ {student.violations} violation{student.violations!==1?'s':''}
            </div>
          )}
        </>
      )}
    </div>
  );
});

const ViolItem = React.memo(function ViolItem({ v }) {
  const [showSnap, setShowSnap] = React.useState(false);
  const hasSnap = v.snapshot_url && v.snapshot_url.startsWith('data:');
  return (
    <div style={{
      padding:'8px 12px', marginBottom:6,
      borderLeft:`3px solid ${v.severity==='critical'?'#ff4444':v.severity==='high'?'var(--accent-danger)':'var(--accent-warning)'}`,
      background:'var(--bg-elevated)', borderRadius:'0 6px 6px 0',
    }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:2 }}>
        <span style={{ fontWeight:600, fontSize:12 }}>{v.name}</span>
        <div style={{ display:'flex', gap:6, alignItems:'center' }}>
          <SevBadge sev={v.severity} />
          <span style={{ fontSize:10, color:'var(--text-muted)' }}>{format(new Date(v.timestamp),'HH:mm:ss')}</span>
        </div>
      </div>
      <div style={{ fontSize:11, color:'var(--text-secondary)' }}>
        {(v.eventType||v.description||'').replace(/_/g,' ')}
        {v.confidence && <span style={{ color:'var(--text-muted)', marginLeft:4 }}>({(v.confidence*100).toFixed(0)}%)</span>}
      </div>
      {hasSnap && (
        <div style={{ marginTop:5 }}>
          <button onClick={() => setShowSnap(s=>!s)}
            style={{ background:'none', border:'1px solid var(--border)', borderRadius:4, padding:'2px 8px', fontSize:10, cursor:'pointer', color:'var(--accent-primary)' }}>
            📷 {showSnap ? 'Hide' : 'View'} Screenshot
          </button>
          {showSnap && (
            <img src={v.snapshot_url} alt="violation"
              style={{ display:'block', marginTop:6, maxWidth:'100%', borderRadius:6, border:'1px solid var(--accent-danger)', cursor:'pointer' }}
              onClick={() => window.open(v.snapshot_url,'_blank')}
              title="Click to open full size" />
          )}
        </div>
      )}
    </div>
  );
});

// ── Camera modal — direct DOM updates for zero-lag streaming ─────────────────
// imgRef and badgeRef are set from parent (LiveMonitor) so WS handler can
// update img.src directly without any React re-render
function CameraModal({ student, frame, onClose, imgRef }) {
  return (
    <div className="modal-overlay" style={{ zIndex:9999 }}>
      <div className="modal" style={{ maxWidth:580, padding:0, overflow:'hidden', borderRadius:'var(--radius-lg)' }}>
        {/* Header */}
        <div style={{ padding:'14px 18px', display:'flex', justifyContent:'space-between', alignItems:'center', borderBottom:'1px solid var(--border)', background:'var(--bg-card)' }}>
          <div>
            <div style={{ fontWeight:700, fontSize:15 }}>📷 {student?.name}</div>
            <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>
              IP: {student?.ip || 'Unknown'} · Live stream ~3fps
            </div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div id="cam-live-badge" style={{ display: frame ? 'flex' : 'none', alignItems:'center', gap:5 }}>
              <div style={{ width:8, height:8, borderRadius:'50%', background:'#10b981', animation:'blink 0.8s infinite' }} />
              <span style={{ fontSize:11, fontWeight:700, color:'#10b981' }}>LIVE</span>
            </div>
            <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:22, lineHeight:1 }}>✕</button>
          </div>
        </div>

        {/* Video frame — img updated directly by WS handler, no re-render */}
        <div style={{ background:'#111', position:'relative', minHeight:280 }}>
          {!frame && (
            <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:12, color:'var(--text-muted)', zIndex:1 }}>
              <div style={{ fontSize:48, opacity:0.3 }}>📷</div>
              <div style={{ fontSize:13, fontWeight:600 }}>Connecting to student camera...</div>
              <div style={{ fontSize:11, opacity:0.6 }}>Frames push automatically · no action needed</div>
            </div>
          )}
          {/* This img src is updated directly by the WS handler — no React state */}
          <img
            ref={imgRef}
            src={frame || ''}
            alt="Student camera live"
            style={{
              width:'100%', display:'block',
              maxHeight:420, objectFit:'contain',
              opacity: frame ? 1 : 0,
              transition: 'opacity 0.15s',
            }}
          />
          {/* Overlays updated via document.getElementById — no re-render */}
          <div style={{ position:'absolute', top:10, left:10, background:'rgba(16,185,129,0.88)', color:'#fff', fontSize:11, fontWeight:700, padding:'2px 10px', borderRadius:20, display: frame ? 'block' : 'none' }}>
            ● LIVE
          </div>
          <div id="cam-ts-overlay" style={{ position:'absolute', bottom:8, right:8, background:'rgba(0,0,0,0.7)', color:'#fff', fontSize:10, padding:'2px 8px', borderRadius:16, fontFamily:'monospace' }}>
            {frame ? new Date().toLocaleTimeString() : ''}
          </div>
        </div>

        <div style={{ padding:'8px 18px', background:'var(--bg-elevated)', fontSize:11, color:'var(--text-muted)', display:'flex', justifyContent:'space-between' }}>
          <span>📡 Direct stream · ~3fps · 240×180</span>
          <span>{student?.name}</span>
        </div>
      </div>
    </div>
  );
}

// ── Send Message Modal (#5 / #11) ─────────────────────────────────────────────
function SendMsgModal({ student, onSend, onClose }) {
  const [text, setText] = useState('');
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:440 }}>
        <h3 className="modal-title">💬 Message to {student?.name}</h3>
        <div className="form-group">
          <label className="form-label">Message</label>
          <textarea className="form-input form-textarea" style={{ minHeight:100 }}
            placeholder="Type a message to the student..."
            value={text} onChange={e=>setText(e.target.value)} autoFocus />
        </div>
        <div className="alert alert-info" style={{ fontSize:12, marginBottom:14 }}>
          This message will appear immediately on the student's exam screen.
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!text.trim()}
            onClick={() => { if (text.trim()) { onSend(text.trim()); onClose(); } }}>
            Send →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Terminate All Modal (#6) ──────────────────────────────────────────────────
function TerminateAllModal({ count, onConfirm, onClose }) {
  const [reason, setReason] = useState('Exam session ended by proctor');
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth:440 }}>
        <h3 className="modal-title">⛔ Terminate All Active Exams</h3>
        <div className="alert alert-danger" style={{ marginBottom:16 }}>
          This will immediately terminate exam for <strong>{count} active student{count!==1?'s':''}</strong>. This cannot be undone.
        </div>
        <div className="form-group">
          <label className="form-label">Reason</label>
          <input className="form-input" value={reason} onChange={e=>setReason(e.target.value)} />
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={() => onConfirm(reason)}>⛔ Terminate All</button>
        </div>
      </div>
    </div>
  );
}

// ─── LiveMonitor ──────────────────────────────────────────────────────────────
export default function LiveMonitor() {
  const { examId } = useParams();
  const navigate   = useNavigate();
  const { user }   = useAuth();
  // #1 FIX: wsRef stable, don't include cameraModal in connect deps
  const wsRef             = useRef(null);
  const reconRef          = useRef(null);
  const cameraModalRef    = useRef(null);
  const latestFramesRef   = useRef({});    // cache: attemptId → {frame, frameTime}
  const liveCanvasRef     = useRef(null);  // <canvas> DOM element in the open modal
  const liveImgRef        = useRef(null);  // <img> DOM element in the open modal

  const [exam,         setExam]         = useState(null);
  const [students,     setStudents]     = useState({});
  const [violations,   setViolations]   = useState([]);
  const [selectedId,   setSelectedId]   = useState(null);
  const [wsStatus,     setWsStatus]     = useState('connecting');
  const [filter,       setFilter]       = useState('all');
  const [sevFilter,    setSevFilter]    = useState('all');
  const [searchQ,      setSearchQ]      = useState('');       // #3 student search
  const [violSearchQ,  setViolSearchQ]  = useState('');       // #3 violation search
  const [cameraModal,  setCameraModal]  = useState(null);
  const [msgModal,     setMsgModal]     = useState(null);
  const [termAllModal, setTermAllModal] = useState(false);    // #6
  const [snapViewer,   setSnapViewer]   = useState(null);     // violation screenshot viewer

  // Keep cameraModalRef in sync for WS handler
  useEffect(() => { cameraModalRef.current = cameraModal; }, [cameraModal]);

  useEffect(() => {
    axios.get('/api/exams/manage').then(r => {
      const ex = r.data.find(e => e.id === examId);
      if (ex) setExam(ex);
    });
  }, [examId]);

  // ── WebSocket — exponential backoff, no duplicate connections ───────────
  const reconnectAttempts = useRef(0);
  const MAX_RECONNECT = 8; // stop after 8 attempts (~4 min total)

  const connect = useCallback(() => {
    const token = sessionStorage.getItem('token');
    if (!token) { setWsStatus('error'); return; }

    // Close any existing connection first — prevents duplicate connections
    if (wsRef.current) {
      wsRef.current.onclose = null; // prevent reconnect loop on manual close
      wsRef.current.onerror = null;
      if (wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }

    // Stop if too many failed attempts (auth issue / server down)
    if (reconnectAttempts.current >= MAX_RECONNECT) {
      setWsStatus('error');
      console.error(`[WS] Stopped after ${MAX_RECONNECT} failed attempts. Check JWT_SECRET and server.`);
      return;
    }

    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const apiHost = process.env.NODE_ENV === 'development'
      ? `${window.location.hostname}:5000`
      : (process.env.REACT_APP_API_HOST || window.location.hostname);

    setWsStatus('connecting');
    const ws = new WebSocket(`${proto}://${apiHost}/ws?token=${token}&role=instructor&examId=${examId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectAttempts.current = 0; // reset on successful connect
      setWsStatus('connected');
    };

    ws.onerror = (err) => {
      console.error('[WS] Connection error:', err);
      setWsStatus('error');
    };

    ws.onclose = (e) => {
      wsRef.current = null;

      // Code 1008 = server rejected (Unauthorized / bad JWT)
      // Code 1006 = abnormal closure (server unreachable)
      if (e.code === 1008) {
        setWsStatus('error');
        console.error('[WS] Unauthorized — check JWT_SECRET matches between frontend and backend');
        return; // Do NOT reconnect on auth failure — it will just loop
      }

      reconnectAttempts.current += 1;
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000); // 2s, 4s, 8s... max 30s
      setWsStatus('reconnecting');
      console.log(`[WS] Closed (code=${e.code}). Reconnect attempt ${reconnectAttempts.current}/${MAX_RECONNECT} in ${delay/1000}s`);
      reconRef.current = setTimeout(connect, delay);
    };

    ws.onmessage = (e) => {
      let msg; try { msg = JSON.parse(e.data); } catch { return; }
      switch (msg.type) {
        case 'snapshot':
          setStudents(() => {
            const map = {};
            (msg.students||[]).forEach(s => { map[s.attemptId] = { ...s }; });
            return map;
          });
          break;
        case 'student_joined':
          setStudents(prev => ({ ...prev, [msg.attemptId]: {
            attemptId:msg.attemptId, name:msg.name, ip:msg.ip,
            answeredCount:0, totalQuestions:0, trustScore:100, violations:0,
            status:'active', lastActivity:msg.timestamp,
          }}));
          break;
        case 'student_heartbeat':
          setStudents(prev => {
            const existing = prev[msg.attemptId] || {};
            return { ...prev, [msg.attemptId]: {
              ...existing,
              ip: msg.ip || existing.ip,
              answeredCount: msg.answeredCount,
              totalQuestions: msg.totalQuestions,
              trustScore: msg.trustScore,
              violations: msg.violations,
              status: 'active',
              lastActivity: msg.timestamp,
            }};
          });
          break;
        case 'student_violation':
          setStudents(prev => ({ ...prev, [msg.attemptId]: {
            ...(prev[msg.attemptId]||{}), trustScore:msg.trustScore, violations:msg.violations,
          }}));
          // Include snapshot in violation feed for instructor to see
          setViolations(prev => [{
            ...msg, id: Date.now(),
            snapshot: msg.snapshot_url || msg.snapshot || null,
          }, ...prev].slice(0, 500));
          break;
        case 'student_idle_change':
          setStudents(prev => ({ ...prev, [msg.attemptId]: { ...(prev[msg.attemptId]||{}), status:msg.status }}));
          break;
        case 'student_submitted':
          setStudents(prev => ({ ...prev, [msg.attemptId]: { ...(prev[msg.attemptId]||{}), status:'submitted' }}));
          break;
        case 'student_disconnected':
          setStudents(prev => ({ ...prev, [msg.attemptId]: { ...(prev[msg.attemptId]||{}), status:'offline' }}));
          break;
        // Camera frame — update DOM directly (no React re-render = zero lag)
        case 'student_camera_frame':
          // Cache latest frame for this student
          latestFramesRef.current[msg.attemptId] = { frame: msg.frame, ts: msg.timestamp };
          // If modal is open for THIS student, paint to img/canvas directly
          const cm = cameraModalRef.current;
          if (cm && cm.attemptId === msg.attemptId) {
            if (liveImgRef.current) {
              liveImgRef.current.src = msg.frame;
            }
            // Update timestamp overlay via DOM
            const tsEl = document.getElementById('cam-ts-overlay');
            if (tsEl) tsEl.textContent = new Date(msg.timestamp).toLocaleTimeString();
            // Update LIVE badge
            const liveEl = document.getElementById('cam-live-badge');
            if (liveEl) liveEl.style.display = 'block';
          }
          break;
      }
    };
  }, [examId]); // #1 FIX: NO cameraModal in deps

  useEffect(() => {
    connect();
    return () => { clearTimeout(reconRef.current); wsRef.current?.close(); };
  }, [connect]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const requestCameraFrame = useCallback((student) => {
    const cached = latestFramesRef.current[student.attemptId];
    const modal = { student, attemptId: student.attemptId, frame: cached?.frame || null, ts: cached?.ts || null };
    cameraModalRef.current = modal;
    setCameraModal(modal); // only sets once to open the modal
    // Trigger an immediate extra push from student
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'request_camera_frame', attemptId: student.attemptId }));
    }
  }, []);

  // refreshCameraFrame removed — frames push automatically every 2s from student


  // #5 & #11: Send message via API (persisted to DB) — WS delivery handled by backend
  const sendMessage = useCallback(async (student, text) => {
    try {
      await axios.post(`/api/attempts/${student.attemptId}/proctor/message`, { text });
      toast.success(`Message sent to ${student.name}`);
    } catch (err) {
      // Fallback: try WS directly
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'message_to_student', attemptId: student.attemptId, text }));
        toast.success(`Message sent to ${student.name} (via WebSocket)`);
      } else {
        toast.error(err.response?.data?.error || 'Failed to send message');
      }
    }
  }, []);

  // #6: Terminate single student
  const terminateStudent = useCallback(async (student) => {
    const reason = `Exam ended by ${user?.full_name || 'Proctor'} (ID: ${user?.id?.slice(0,8)||'?'}) — CI Ended the Exam`;
    try {
      await axios.post(`/api/attempts/${student.attemptId}/terminate`, { reason });
      toast.success(`Terminated: ${student.name}`);
      setStudents(prev => ({ ...prev, [student.attemptId]: { ...prev[student.attemptId], status:'terminated' } }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to terminate');
    }
  }, [user]);

  // #6: Terminate ALL active students
  const terminateAll = useCallback(async (reason) => {
    const active = Object.values(students).filter(s => s.status === 'active' || s.status === 'idle');
    setTermAllModal(false);
    let done = 0;
    for (const s of active) {
      try {
        await axios.post(`/api/attempts/${s.attemptId}/terminate`, { reason });
        setStudents(prev => ({ ...prev, [s.attemptId]: { ...prev[s.attemptId], status:'terminated' } }));
        done++;
      } catch { /* continue */ }
    }
    toast.success(`Terminated ${done} exam${done!==1?'s':''}`);
  }, [students]);

  // Resume
  const [resumeTarget, setResumeTarget] = useState(null);
  const [resumeMins,   setResumeMins]   = useState('0');

  const resumeExam = useCallback((student) => {
    setResumeTarget(student);
    setResumeMins('0');
  }, []);

  const confirmResume = useCallback(async () => {
    if (!resumeTarget) return;
    try {
      await axios.post(`/api/attempts/${resumeTarget.attemptId}/resume`, {
        extra_minutes: parseInt(resumeMins) || 0,
      });
      toast.success(`✅ Resumed: ${resumeTarget.name}`);
      setStudents(prev => ({ ...prev, [resumeTarget.attemptId]: { ...prev[resumeTarget.attemptId], status:'active' } }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to resume');
    } finally {
      setResumeTarget(null);
    }
  }, [resumeTarget, resumeMins, setStudents]);

  // ── Derived / filtered data ───────────────────────────────────────────────
  const studentList = useMemo(() => Object.values(students), [students]);

  // #3 Student search + status filter
  const filteredStudents = useMemo(() => {
    let list = studentList;
    if (searchQ) list = list.filter(s => s.name?.toLowerCase().includes(searchQ.toLowerCase()) || s.ip?.includes(searchQ));
    if (filter === 'active')     list = list.filter(s => s.status === 'active');
    else if (filter === 'idle')  list = list.filter(s => s.status === 'idle');
    else if (filter === 'flagged') list = list.filter(s => (s.violations||0) > 0);
    else if (filter === 'offline') list = list.filter(s => s.status==='offline'||s.status==='disconnected');
    else if (filter === 'terminated') list = list.filter(s => s.status==='terminated');
    return list;
  }, [studentList, searchQ, filter]);

  const selected = selectedId ? students[selectedId] : null;

  // #3 Violation filter with search
  const filteredViolations = useMemo(() => {
    let list = selectedId ? violations.filter(v => v.attemptId === selectedId) : violations;
    if (sevFilter !== 'all') list = list.filter(v => v.severity === sevFilter);
    if (violSearchQ) list = list.filter(v =>
      v.name?.toLowerCase().includes(violSearchQ.toLowerCase()) ||
      v.description?.toLowerCase().includes(violSearchQ.toLowerCase()) ||
      v.eventType?.toLowerCase().includes(violSearchQ.toLowerCase())
    );
    return list;
  }, [violations, selectedId, sevFilter, violSearchQ]);

  const counts = useMemo(() => ({
    active:     studentList.filter(s=>s.status==='active').length,
    idle:       studentList.filter(s=>s.status==='idle').length,
    submitted:  studentList.filter(s=>s.status==='submitted').length,
    offline:    studentList.filter(s=>s.status==='offline'||s.status==='disconnected').length,
    flagged:    studentList.filter(s=>(s.violations||0)>0).length,
    terminated: studentList.filter(s=>s.status==='terminated').length,
  }), [studentList]);

  const wsColors = { connected:'var(--accent-success)', reconnecting:'var(--accent-warning)', error:'var(--accent-danger)', connecting:'var(--text-muted)' };

  const activeCount = counts.active + counts.idle;

  return (
    <div className="layout">
      <Sidebar />
      <main className="main-content" style={{ padding:20 }}>

        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:18 }}>
          <div>
            <div style={{ fontSize:11, fontFamily:'var(--font-mono)', color:'var(--text-muted)', letterSpacing:'1px', marginBottom:3 }}>LIVE MONITORING</div>
            <h1 style={{ fontSize:20, fontWeight:700, margin:0 }}>{exam?.title || 'Loading...'}</h1>
            <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:3 }}>
              Proctor: <strong>{user?.full_name}</strong> · ID: <span style={{ fontFamily:'var(--font-mono)' }}>{user?.id?.slice(0,8)}</span>
            </div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12 }}>
              <div style={{ width:8, height:8, borderRadius:'50%', background:wsColors[wsStatus], animation:wsStatus==='connected'?'blink 2s infinite':'none' }} />
              <span style={{ color:wsColors[wsStatus], fontWeight:600 }}>{wsStatus==='connected'?'LIVE':wsStatus.toUpperCase()}</span>
            </div>
            {/* #6 Terminate All */}
            {activeCount > 0 && (
              <button className="btn btn-danger btn-sm" onClick={() => setTermAllModal(true)}>
                ⛔ End All ({activeCount})
              </button>
            )}
            {/* #9 Export */}
            <button className="btn btn-secondary btn-sm" onClick={() => {
              const rows = studentList.map(s => `"${s.name}","${s.ip||''}","${s.status}","${s.answeredCount||0}/${s.totalQuestions||0}","${Math.round(s.trustScore||100)}%","${s.violations||0}"`);
              const csv = ['Name,IP,Status,Progress,Trust,Violations', ...rows].join('\n');
              const a = document.createElement('a');
              a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
              a.download = `monitor-${Date.now()}.csv`;
              a.click();
            }}>⬇ Export</button>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/dashboard')}>← Back</button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:8, marginBottom:16 }}>
          {[
            ['Active',     counts.active,     'var(--accent-success)'],
            ['Idle>5m',    counts.idle,       '#ffffff'],
            ['Submitted',  counts.submitted,  'var(--accent-primary)'],
            ['Flagged',    counts.flagged,    'var(--accent-danger)'],
            ['Terminated', counts.terminated, 'var(--accent-danger)'],
            ['Offline',    counts.offline,    'var(--text-muted)'],
          ].map(([label,val,color]) => (
            <div key={label} style={{ padding:'10px 12px', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:8 }}>
              <div style={{ fontSize:22, fontWeight:700, fontFamily:'var(--font-mono)', color }}>{val}</div>
              <div style={{ fontSize:10, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px' }}>{label}</div>
            </div>
          ))}
        </div>

        {/* #3 Filters row */}
        <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
          {/* #10 Student search */}
          <input className="form-input" style={{ width:200, padding:'7px 12px', fontSize:13 }}
            placeholder="🔍 Name or IP..."
            value={searchQ} onChange={e => setSearchQ(e.target.value)} />
          {/* Status filters */}
          {[['all','All'],['active','Active'],['idle','Idle'],['flagged','Flagged'],['terminated','Terminated'],['offline','Offline']].map(([val,label]) => (
            <button key={val} className="btn btn-secondary btn-sm"
              onClick={() => setFilter(val)}
              style={filter===val ? { borderColor:'var(--accent-primary)', color:'var(--accent-primary)', background:'rgba(79,142,247,0.1)' } : {}}>
              {label}
            </button>
          ))}
        </div>

        <div style={{ display:'grid', gridTemplateColumns: selected ? '1fr 360px' : '1fr', gap:18, alignItems:'start' }}>

          {/* Student grid */}
          <div>
            {filteredStudents.length === 0 ? (
              <div style={{ textAlign:'center', padding:40, color:'var(--text-secondary)', background:'var(--bg-card)', borderRadius:'var(--radius-lg)', border:'1px solid var(--border)' }}>
                {wsStatus === 'connected' && 'No students match your filters'}
                {wsStatus === 'connecting' && '⏳ Connecting to live feed...'}
                {wsStatus === 'reconnecting' && (
                  <div>
                    <div style={{ fontSize:28, marginBottom:10 }}>🔄</div>
                    <div style={{ fontWeight:600, marginBottom:6 }}>Reconnecting...</div>
                    <div style={{ fontSize:13, color:'var(--text-muted)' }}>
                      Attempt {reconnectAttempts.current}/{8} — check server connection
                    </div>
                  </div>
                )}
                {wsStatus === 'error' && (
                  <div>
                    <div style={{ fontSize:32, marginBottom:10 }}>🔌</div>
                    <div style={{ fontWeight:700, fontSize:16, color:'var(--accent-danger)', marginBottom:8 }}>
                      WebSocket Connection Failed
                    </div>
                    <div style={{ fontSize:13, color:'var(--text-muted)', maxWidth:380, margin:'0 auto 16px', lineHeight:1.7 }}>
                      Could not connect to the live monitoring server.<br/>
                      Most likely cause: <strong>JWT_SECRET mismatch</strong> between frontend token and backend.
                    </div>
                    <div style={{ fontSize:12, padding:'10px 14px', background:'rgba(239,68,68,0.08)', borderRadius:8, border:'1px solid rgba(239,68,68,0.2)', maxWidth:380, margin:'0 auto 16px', textAlign:'left' }}>
                      <div style={{ fontWeight:600, marginBottom:6, color:'var(--accent-danger)' }}>Check on your server:</div>
                      <code style={{ fontSize:11, fontFamily:'monospace', color:'var(--text-secondary)' }}>
                        cat /var/www/AI-Exam-System/backend/.env | grep JWT_SECRET
                      </code>
                      <div style={{ marginTop:8, fontSize:11, color:'var(--text-muted)' }}>
                        Then logout and login again to get a new token with the correct secret.
                      </div>
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={() => { reconnectAttempts.current = 0; connect(); }}>
                      🔄 Retry Connection
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(210px,1fr))', gap:10 }}>
                {filteredStudents.map(s => (
                  <StudentCard key={s.attemptId} student={s}
                    selected={selectedId === s.attemptId}
                    onSelect={id => setSelectedId(prev => prev === id ? null : id)} />
                ))}
              </div>
            )}
          </div>

          {/* Detail panel */}
          {selected && (
            <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:18, position:'sticky', top:20, maxHeight:'calc(100vh - 160px)', overflowY:'auto', display:'flex', flexDirection:'column', gap:12 }}>

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ fontWeight:700, fontSize:15 }}>{selected.name}</div>
                <button onClick={() => setSelectedId(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:18 }}>✕</button>
              </div>

              {/* Stats */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                {[
                  { label:'IP Address', val: selected.ip || 'Unknown', mono:true,
                    color: selected.status==='idle'?'#ffffff':selected.status==='active'?'var(--accent-success)':'var(--text-secondary)' },
                  { label:'Status', val: (selected.status||'—').toUpperCase(),
                    color: selected.status==='active'?'var(--accent-success)':selected.status==='idle'?'#ffffff':'var(--accent-danger)' },
                  { label:'Progress', val: `${selected.answeredCount||0}/${selected.totalQuestions||'?'}` },
                  { label:'Violations', val: selected.violations||0,
                    color: (selected.violations||0)>0?'var(--accent-danger)':'var(--text-secondary)' },
                ].map(item => (
                  <div key={item.label} style={{ background:'var(--bg-elevated)', padding:'8px 10px', borderRadius:8, border:'1px solid var(--border)' }}>
                    <div style={{ fontSize:10, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:3 }}>{item.label}</div>
                    <div style={{ fontSize:13, fontWeight:700, fontFamily:item.mono?'var(--font-mono)':'inherit', color:item.color||'var(--text-primary)' }}>{item.val}</div>
                  </div>
                ))}
              </div>

              <TrustBar score={selected.trustScore ?? 100} />

              {/* Action buttons — #6 terminate, camera, message, resume */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                <button className="btn btn-secondary btn-sm" style={{ justifyContent:'center' }}
                  onClick={() => requestCameraFrame(selected)}>
                  📷 View Camera
                </button>
                <button className="btn btn-secondary btn-sm" style={{ justifyContent:'center', color:'var(--accent-primary)', borderColor:'rgba(79,142,247,0.3)' }}
                  onClick={() => setMsgModal(selected)}>
                  💬 Message
                </button>
                {(selected.status === 'active' || selected.status === 'idle') && (
                  <button className="btn btn-danger btn-sm" style={{ justifyContent:'center', gridColumn:'1/-1' }}
                    onClick={() => terminateStudent(selected)}>
                    ⛔ Terminate (CI)
                  </button>
                )}
                {selected.status === 'terminated' && (
                  <button className="btn btn-success btn-sm" style={{ justifyContent:'center', gridColumn:'1/-1' }}
                    onClick={() => resumeExam(selected)}>
                    ▶ Resume Exam
                  </button>
                )}
                <button className="btn btn-secondary btn-sm" style={{ justifyContent:'center', gridColumn:'1/-1' }}
                  onClick={() => navigate(`/proctor/${selected.attemptId}`)}>
                  View Full Report →
                </button>
              </div>

              {/* #3 Violation filter with search */}
              <div>
                <div style={{ fontWeight:600, fontSize:12, color:'var(--text-secondary)', marginBottom:8 }}>
                  Violation History
                </div>
                <div style={{ display:'flex', gap:6, marginBottom:8 }}>
                  {/* #3 violation search */}
                  <input className="form-input" style={{ flex:1, padding:'5px 8px', fontSize:12 }}
                    placeholder="Search violations..."
                    value={violSearchQ} onChange={e => setViolSearchQ(e.target.value)} />
                  {/* #7 severity filter */}
                  <select className="form-input form-select" style={{ padding:'5px 8px', fontSize:11, width:80 }}
                    value={sevFilter} onChange={e => setSevFilter(e.target.value)}>
                    <option value="all">All</option>
                    <option value="critical">Crit</option>
                    <option value="high">High</option>
                    <option value="medium">Med</option>
                    <option value="low">Low</option>
                  </select>
                </div>
                {filteredViolations.length === 0 ? (
                  <div style={{ fontSize:12, color:'var(--accent-success)', textAlign:'center', padding:'10px 0' }}>✓ No violations</div>
                ) : (
                  <div style={{ maxHeight:260, overflowY:'auto' }}>
                    {filteredViolations.slice(0,50).map((v,i) => <ViolItem key={v.id||i} v={v} onViewSnapshot={setSnapViewer} />)}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Global violation feed (when no student selected) */}
        {!selected && violations.length > 0 && (
          <div style={{ marginTop:20 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
              <div style={{ fontWeight:600, fontSize:14, color:'var(--text-secondary)' }}>
                Live Violation Feed ({filteredViolations.length})
              </div>
              <div style={{ display:'flex', gap:8 }}>
                {/* #3 global violation search */}
                <input className="form-input" style={{ width:180, padding:'5px 10px', fontSize:12 }}
                  placeholder="🔍 Search events..."
                  value={violSearchQ} onChange={e => setViolSearchQ(e.target.value)} />
                <select className="form-input form-select" style={{ padding:'5px 10px', fontSize:12, width:'auto' }}
                  value={sevFilter} onChange={e => setSevFilter(e.target.value)}>
                  <option value="all">All Severity</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:8, maxHeight:300, overflowY:'auto' }}>
              {filteredViolations.slice(0,40).map((v,i) => <ViolItem key={v.id||i} v={v} onViewSnapshot={setSnapViewer} />)}
            </div>
          </div>
        )}
      </main>

      {cameraModal && (
        <CameraModal
          student={cameraModal.student}
          frame={cameraModal.frame}
          imgRef={liveImgRef}
          onClose={() => {
            cameraModalRef.current = null;
            liveImgRef.current = null;
            setCameraModal(null);
          }} />
      )}

      {msgModal && (
        <SendMsgModal
          student={msgModal}
          onSend={text => sendMessage(msgModal, text)}
          onClose={() => setMsgModal(null)} />
      )}

      {/* Violation screenshot full-view modal */}
      {snapViewer && (
        <div className="modal-overlay" style={{ zIndex:10000 }} onClick={() => setSnapViewer(null)}>
          <div style={{ maxWidth:700, margin:'auto', position:'relative' }} onClick={e=>e.stopPropagation()}>
            <button onClick={() => setSnapViewer(null)}
              style={{ position:'absolute', top:-36, right:0, background:'rgba(255,255,255,0.15)', border:'none', color:'#fff', fontSize:22, cursor:'pointer', borderRadius:8, padding:'2px 10px' }}>✕</button>
            <img src={snapViewer} alt="Violation evidence"
              style={{ width:'100%', borderRadius:10, boxShadow:'0 20px 60px rgba(0,0,0,0.6)', display:'block' }} />
            <div style={{ textAlign:'center', marginTop:10 }}>
              <a href={snapViewer} download={`violation-${Date.now()}.jpg`}
                className="btn btn-secondary btn-sm" style={{ color:'var(--accent-primary)' }}>
                ⬇ Download Screenshot
              </a>
            </div>
          </div>
        </div>
      )}

      {termAllModal && (
        <TerminateAllModal
          count={activeCount}
          onConfirm={terminateAll}
          onClose={() => setTermAllModal(false)} />
      )}

      {/* FIX 6: Resume individual student modal */}
      {resumeTarget && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth:420 }}>
            <h3 className="modal-title" style={{ marginBottom:6 }}>✅ Resume Exam</h3>
            <p style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:16 }}>
              Resuming exam for <strong>{resumeTarget.name}</strong>. The student will receive an instant notification.
            </p>
            <div className="form-group">
              <label className="form-label">Extra time to grant (minutes)</label>
              <input className="form-input" type="number" min={0} max={180}
                value={resumeMins}
                onChange={e => setResumeMins(e.target.value)}
                placeholder="0 = resume with remaining time only" />
              <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:5 }}>
                Enter 0 to resume with exactly the time the student had left. Enter a positive number to add extra minutes.
              </div>
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
              <button className="btn btn-secondary" onClick={() => setResumeTarget(null)}>Cancel</button>
              <button className="btn btn-success" onClick={confirmResume}>✅ Resume Exam</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
